import { useMemo, useState, type ReactNode } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useResourceKit } from '@resource-kit/core/react';
import { DataTable } from './DataTable';
import { SearchView, type SearchViewCapabilities } from './SearchView';
import { useColumnVisibility } from './useColumnVisibility';
import { LIST_LAYOUT, useListLayout } from './useListLayout';
import { useKitIsPhone } from './useKitIsPhone';
import { Card } from '../primitives/Card';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Button } from '../primitives/Button';
import type { ColumnSchema, Condition } from '@resource-kit/model';
import {
  calendarDayIn,
  deriveQueryBuilderColumns,
  deriveQueryColumnsFromFields,
  downloadCsv,
  type ConditionMergeNotice,
  type FilterWhitelist,
} from '@resource-kit/controllers';
import type { EntityField, ListCardRenderer } from '@resource-kit/controllers/react';
import { tableToCsv } from './table-export';
import { createPortal } from 'react-dom';
import {
  usePrintJob,
  type ListPrintConfig,
  type PrintScope,
} from '@resource-kit/controllers/react';
import { PrintOptionsDialog } from '../resource/print/PrintOptionsDialog';
import { PrintSheet } from '../resource/print/PrintSheet';

/**
 * Which of the three list states the data is in (#1801, design §6.4) — now
 * declared in `@resource-kit/controllers` (#2167) alongside `ListResult`, and
 * re-exported here so this path keeps serving it.
 */
import type { ListStatus } from '@resource-kit/controllers';

export type { ListStatus };

export interface ListViewProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  rows: TData[];
  pagination: {
    total: number;
    totalPages: number;
    page: number;
    limit: number;
  };
  status: ListStatus;
  /** Failure detail; only meaningful when `status === 'error'`. */
  error?: string | null;
  /** Recovery action for the error state. Without it there is no way out. */
  onRetry?: () => void;

  /**
   * Whether the operator has narrowed the list (free-text search or any
   * active condition). This is what separates "this resource is empty" from
   * "your filter matched nothing" — two states that look identical and mean
   * opposite things.
   */
  isFiltered: boolean;
  /** Clears search + conditions in ONE transition. */
  onClearFilters?: () => void;
  /**
   * Fields whose filter the backend whitelist rejected. Non-empty means the
   * visible list is NOT filtered the way the UI claims, so it is announced.
   */
  droppedFilters?: readonly string[];
  /**
   * Same-field condition collapses reported by the controller (#1803, design
   * §5.9). Non-empty means two or more conditions targeted one field and were
   * combined (or the last one won) — announced above the table, never a
   * silent drop.
   */
  mergeNotices?: readonly ConditionMergeNotice[];
  /** Headline for the unfiltered empty state. Defaults to the generic one. */
  emptyLabel?: string;
  /** Primary action offered alongside the unfiltered empty state. */
  emptyAction?: ReactNode;

  onPageChange: (page: number) => void;
  /**
   * Page-size handler. OMIT it to opt out of the rows-per-page control
   * entirely: with no handler DataTable renders no page-size Select at all
   * (rather than an interactive-looking control whose input is swallowed).
   */
  onLimitChange?: (limit: number) => void;
  onSearchChange?: (search: string) => void;
  // Sort / filter wiring is optional: a table that only supports search +
  // pagination omits these rather than passing inert handlers.
  onSortChange?: (field: string) => void;
  onConditionsChange?: (conditions: Condition[]) => void;

  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  conditions?: Condition[];

  searchPlaceholder?: string;
  /**
   * Whether the free-text search box is offered. Defaults to `true`. The box
   * coexists with the QueryBuilder; free-text search and structured
   * conditions combine with AND semantics. Pass `false` for a resource whose
   * backend list route has no `search` parameter — a visible box that only
   * filters the current page's rows is a lie.
   */
  searchable?: boolean;
  /**
   * The controller's resolved capability set (#1803, design §6.6). When
   * supplied it decides which surfaces `SearchView` offers; when omitted the
   * legacy props (`searchable`, plus "a schema means a filter builder") keep
   * deciding, so callers that predate the three-source split are unaffected.
   *
   * Capabilities are opt-in per item and do NOT change this contract — there
   * is no "ListView with search" type.
   */
  capabilities?: Partial<SearchViewCapabilities>;
  onRowClick?: (row: TData) => void;
  selectable?: boolean;
  onSelectionChange?: (rows: TData[]) => void;
  selectionResetKey?: number;
  getRowId?: (row: TData) => string;
  /**
   * The resource's `EntityField` list. When provided the QueryBuilder schema
   * is derived STRAIGHT from the fields (#1407 R3) rather than from the
   * `ColumnDef.meta.filter` projection — so a column hidden in the list can
   * still be filtered. Omitted by hand-built ColumnDef tables.
   */
  fields?: readonly EntityField<TData>[];
  queryBuilderColumns?: ColumnSchema[];
  /**
   * The resource's backend `filterable` whitelist. When provided the
   * QueryBuilder only offers columns the backend accepts and trims each
   * column's ops to its whitelist, so the UI can never surface a filter the
   * `list` adapter would silently drop.
   */
  filterable?: FilterWhitelist;
  /**
   * Base name for the exported CSV file (a `-YYYY-MM-DD.csv` suffix is
   * appended). Typically the resource slug; defaults to `export`.
   */
  exportFileName?: string;
  /**
   * Base key for the list's stored per-viewer preferences — the column
   * visibility (`columns.<key>`) and the table/cards layout
   * (`listLayout.<key>`) (#2680). Absent ⇒ `exportFileName`, which keeps the
   * keys every list wrote before this prop existed. CSV file names and the
   * print title keep using `exportFileName`.
   */
  preferenceKey?: string;
  /**
   * Optional "export all data" provider. When supplied the export dropdown
   * offers "Export all data (CSV)" alongside "Export selected"; the handler
   * resolves the FULL row set for the current query. A rejection surfaces a
   * toast and no file is written.
   */
  onExportAll?: () => Promise<TData[]>;
  /**
   * The rows currently ticked, for the selection-scoped export. A PROP, not
   * state: the selection lives in the table and is owned by whoever wired
   * `onSelectionChange` (ResourcePage keeps it in `bulk.selectedRows`).
   * Mirroring it here would make a second truth for the same fact.
   */
  selectedRows?: TData[];
  /**
   * Printing, supplied by the callsite. Absent ⇒ no print control at all:
   * unlike a CSV, a sheet cannot be derived from `columns` in a form
   * anybody wants without someone having looked at it.
   */
  print?: ListPrintConfig<TData>;
  /** Quick-filter presets rendered at the top of the toolbar. */
  presetSlot?: ReactNode;
  /** A resource's own card layout (#2680); absent ⇒ the default card. */
  renderCard?: ListCardRenderer<TData>;
  /**
   * Where a card's title row goes (#2680). Absent, or answering `undefined`,
   * ⇒ the title row falls back to `onRowClick`. The table row is unaffected.
   */
  getRowHref?: (row: TData) => string | undefined;
}

/** Non-interactive placeholder rows sized to the page the operator asked for. */
function ListSkeleton({ rows, columns }: { rows: number; columns: number }) {
  const { t } = useResourceKit();
  return (
    <div
      data-testid="list-skeleton"
      role="status"
      aria-busy="true"
      aria-label={t('rk.table.loading')}
      className="space-y-2 py-2"
    >
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} data-testid="list-skeleton-row" className="flex items-center gap-3">
          {Array.from({ length: columns }, (__, cell) => (
            <div key={cell} className="h-4 flex-1 animate-pulse rounded bg-muted" />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * The list renderer (#1801).
 *
 * Pure in the sense that matters: it owns no URL state and fetches nothing.
 * Rows, pagination, status and every handler arrive as props from a controller
 * (`useRemoteList`, bulk `useUrlOwnedList`, or `useLocalList`). What
 * it does own is presentation-local state that no controller should carry:
 * persisted column visibility and the export-in-flight guard.
 */
export function ListView<TData, TValue>({
  columns,
  rows,
  pagination,
  status,
  error,
  onRetry,
  isFiltered,
  onClearFilters,
  droppedFilters,
  mergeNotices,
  emptyLabel,
  emptyAction,
  onPageChange,
  onLimitChange,
  onSortChange,
  onSearchChange,
  onConditionsChange = () => {},
  sortField,
  sortOrder,
  search = '',
  conditions = [],
  searchPlaceholder,
  searchable = true,
  capabilities,
  onRowClick,
  selectable = false,
  onSelectionChange,
  selectionResetKey,
  getRowId,
  fields,
  queryBuilderColumns,
  filterable,
  exportFileName,
  onExportAll,
  selectedRows,
  print: printConfig,
  presetSlot,
  preferenceKey,
  renderCard,
  getRowHref,
}: ListViewProps<TData, TValue>) {
  const { t, format, notify } = useResourceKit();
  const exportCtx = useMemo(() => ({ t, format }), [t, format]);
  const prefBase = preferenceKey ?? (exportFileName || 'export');
  const visibility = useColumnVisibility(columns, prefBase);
  // The table/cards choice (#2680). A phone always gets cards and no
  // selection: a table scrolls sideways there and a checkbox column has no
  // room. The layout never reaches the CSV or print paths below — those read
  // `columns` and the column visibility only.
  const layout = useListLayout(prefBase);
  const isPhone = useKitIsPhone();
  // "Export all" in-flight guard — disables the menu item so a slow
  // multi-page pull can't be re-triggered into overlapping downloads.
  const [isExportingAll, setIsExportingAll] = useState(false);

  const selection = selectedRows ?? [];
  const handleExportSelected = () => {
    if (selection.length === 0) return;
    const csv = tableToCsv(columns, selection, visibility.columnVisibility, exportCtx);
    // The file name carries the merchant's calendar day, not the UTC day (#2875).
    const date = calendarDayIn(format.timeZone, new Date());
    downloadCsv(`${exportFileName || 'export'}-selected-${date}.csv`, csv);
  };

  const handleExportAll = async () => {
    if (!onExportAll || isExportingAll) return;
    setIsExportingAll(true);
    try {
      const allRows = await onExportAll();
      const csv = tableToCsv(columns, allRows, visibility.columnVisibility, exportCtx);
      const date = calendarDayIn(format.timeZone, new Date());
      downloadCsv(`${exportFileName || 'export'}-all-${date}.csv`, csv);
    } catch (err) {
      notify(
        'error',
        t('rk.table.exportAllFailed'),
        err instanceof Error ? err.message : undefined,
      );
    } finally {
      setIsExportingAll(false);
    }
  };

  const printTitle = t('rk.print.sheet.title', { resource: exportFileName || 'export' });

  const printJob = usePrintJob<TData>({
    config: printConfig,
    fetchAllRows: onExportAll,
    selectedRows: selection,
    // The built-in sheet lives here because it needs `columns` and the
    // resolved column visibility, neither of which exists outside this
    // component.
    renderDefault: (printRows) => (
      <PrintSheet
        columns={columns}
        rows={printRows}
        visibility={visibility.columnVisibility}
        ctx={exportCtx}
        title={printTitle}
      />
    ),
    documentTitle: printTitle,
  });

  // Derive the QueryBuilder schema, trimmed to the resource's backend
  // `filterable` whitelist. Framework pages pass their `EntityField` list and
  // derive straight from it (#1407 R3); hand-built ColumnDef tables fall back
  // to the meta.filter projection.
  const effectiveSchema: ColumnSchema[] | null = useMemo(() => {
    let derivedOrNull: ColumnSchema[] | null;
    try {
      derivedOrNull = fields
        ? deriveQueryColumnsFromFields(fields, filterable)
        : deriveQueryBuilderColumns(columns, filterable);
    } catch {
      // A field definition the schema deriver cannot map must cost the
      // page its filter builder, not the whole list. Unchanged from the
      // pre-split behaviour.
      derivedOrNull = null;
    }
    const derived = derivedOrNull ?? [];
    const extras = queryBuilderColumns || [];
    if (extras.length === 0 && derived.length === 0) return null;
    const map = new Map<string, ColumnSchema>();
    for (const d of derived) map.set(d.id, d);
    for (const e of extras) map.set(e.id, { ...map.get(e.id), ...e });
    return Array.from(map.values());
  }, [fields, filterable, columns, queryBuilderColumns]);

  // Stable key describing the current query context (the selection context).
  // Selection is page-local: it must reset when the operator moves to a
  // different slice of data — but NOT when the framework background-refetches
  // the SAME page. These params fully describe "which slice is on screen", so
  // a same-page refetch leaves the key unchanged and id-keyed selection
  // survives. Uses the COMMITTED `search`, not the toolbar's draft.
  const selectionContextKey = JSON.stringify({
    page: pagination.page,
    limit: pagination.limit,
    sortField,
    sortOrder,
    search,
    conditions,
  });

  // The search / filter surface, plus the two "what you see is not what was
  // applied" warnings (§6.4): a filter the backend refused, and same-field
  // conditions that collapsed. Capabilities decide which surfaces exist —
  // falling back to the legacy props so pre-#1803 callers are unchanged.
  const searchView = (
    <SearchView
      capabilities={{
        search: capabilities?.search ?? searchable,
        filter: capabilities?.filter ?? true,
      }}
      presetSlot={presetSlot}
      searchPlaceholder={searchPlaceholder}
      search={search}
      onSearchChange={onSearchChange}
      schema={effectiveSchema}
      conditions={conditions}
      onConditionsChange={onConditionsChange}
      droppedFilters={droppedFilters}
      mergeNotices={mergeNotices}
      visibility={visibility}
      onExportSelected={handleExportSelected}
      exportSelectedDisabled={selection.length === 0}
      onExportAll={onExportAll ? handleExportAll : undefined}
      exportAllDisabled={isExportingAll}
      onPrint={printConfig ? (scope: PrintScope) => printJob.start(scope) : undefined}
      printSelectedDisabled={selection.length === 0}
      layoutToggle={
        isPhone ? undefined : { layout: layout.layout, onLayoutChange: layout.setLayout }
      }
    />
  );

  // §6.4's state table, in order. Each branch is exclusive by construction:
  // an error never falls through to a table, and a first load never falls
  // through to an empty state.
  const body = (() => {
    if (status === 'error') {
      return (
        <div data-testid="list-error">
          <ErrorState
            message={error ?? t('rk.error.unknown')}
            className="py-8"
            action={
              onRetry ? (
                <Button variant="outline" size="sm" data-testid="list-retry" onClick={onRetry}>
                  {t('rk.ui.retry')}
                </Button>
              ) : undefined
            }
          />
        </div>
      );
    }

    if (status === 'loading' && rows.length === 0) {
      return (
        <ListSkeleton
          rows={Math.max(1, Math.min(pagination.limit, 25))}
          columns={Math.max(1, columns.length)}
        />
      );
    }

    const emptyContent = isFiltered ? (
      <EmptyState
        className="py-0"
        title={t('rk.table.noMatchingResults')}
        action={
          onClearFilters ? (
            <Button
              variant="outline"
              size="sm"
              data-testid="list-clear-filters"
              onClick={onClearFilters}
            >
              {t('rk.table.clearFilters')}
            </Button>
          ) : undefined
        }
      />
    ) : (
      <EmptyState
        className="py-0"
        title={emptyLabel ?? t('rk.table.noResultsFound')}
        action={emptyAction}
      />
    );

    return (
      <DataTable
        data={rows}
        columns={columns}
        loading={status === 'loading'}
        pagination={{
          currentPage: pagination.page,
          totalPages: pagination.totalPages,
          onPageChange,
          total: pagination.total,
          pageSize: pagination.limit,
          onPageSizeChange: onLimitChange,
        }}
        onSort={onSortChange}
        sortField={sortField}
        sortOrder={sortOrder}
        onRowClick={onRowClick}
        selectable={selectable && !isPhone}
        layout={isPhone ? LIST_LAYOUT.cards : layout.layout}
        renderCard={renderCard}
        getRowHref={getRowHref}
        onSelectionChange={onSelectionChange}
        selectionResetKey={selectionResetKey}
        selectionContextKey={selectionContextKey}
        getRowId={getRowId}
        columnVisibility={visibility.columnVisibility}
        onColumnVisibilityChange={visibility.setColumnVisibility}
        emptyContent={
          <div data-testid={isFiltered ? 'list-empty-filtered' : 'list-empty'}>{emptyContent}</div>
        }
      />
    );
  })();

  return (
    <div>
      <Card className="p-4">
        {searchView}
        {body}
      </Card>
      {printConfig?.options?.length ? (
        <PrintOptionsDialog
          open={printJob.phase === 'configuring'}
          defs={printConfig.options}
          values={printJob.options}
          onChange={printJob.setOption}
          onConfirm={() => void printJob.confirm()}
          onCancel={printJob.cancel}
          error={printJob.error}
          busy={printJob.phase === 'loading'}
        />
      ) : null}
      {/* The sheet renders into a SEPARATE window (#1902), not into this
                tree and not into a portal beside it. That window's document is
                self-contained — its own `@page`, its own colours, no theme
                tokens — and the operator prints it from the browser's own
                dialog, so there is no toolbar to render over it. */}
      {printJob.sheet !== null && printJob.mount
        ? createPortal(printJob.sheet, printJob.mount)
        : null}
    </div>
  );
}
